import {
	Component,
	OnInit,
	OnDestroy,
	Input,
	ViewChild,
	AfterViewInit
} from '@angular/core';
import { Location } from '@angular/common';
import { IMyDpOptions } from 'mydatepicker';
import { DragulaService } from 'ng2-dragula';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import {
	Quotes,
	Product,
	PaymentSchedule,
	Contract,
	JobQuestionnarie,
	QuotePayments,
	QuoteType
} from '@models/models';
import { ContractsDataProvider } from '@services/contract/contracts.data.service';
import { TemplatesDataProvider } from '@services/data-services/templates-data-service/templates.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { StringModifierServiceProvider } from '@services/helpers/string-modifier-service/stringModifier.service';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { JobsDataServiceProvider } from '@services/job/jobs.data.service';
import { LeadsDataServiceProvider } from '@services/lead/leads.data.service';
import { PaymentSchedulesDataProvider } from '@services/payments/payments-schedules-data-service/payments-schedules.data.service';
import { QuestionnairesDataProvider } from '@services/questionnarie/questionnaires.data.service';
import { QuotesDataProvider } from '@services/quote/quotes-data-service/quotes.data.service';
import { ProductPackagesDataServiceProvider } from '@services/settings/product-packages.data.service';
import { SettingsProvider } from '@services/settings/settings';
import { QuoteUpdateService } from '@services/quote/quote-update.service';
import { PaymentScheduleComponent } from '@web-components/quotes/common/payment-schedule/payment-schedule';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
	selector: 'quote-update',
	template: require('./quote-update.html'),
	styles: [require('./quote-update.scss')]
})
export class QuoteUpdateComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input('idToGetQuote') idToGetQuote: number;
	@Input('isJob') isJob: boolean;
	@Input('quoteId') quoteId: number;
	@Input('quoteType') quoteType: QuoteType;
	@ViewChild('paymentScheduleComponent')
	paymentScheduleComponent: PaymentScheduleComponent;
	buttonDisable = false;
	datePlaceHolder = '';

	autoGenerateInvoice = true;
	quote: Quotes;
	packages: Array<Product> = [];
	addons: Array<Product> = [];
	discounts: Array<Product> = [];
	currencyAndTax;
	paymentSchedule: PaymentSchedule;
	paymentScheduleTemplateId = 0;
	paymentScheduleIdFromServer: number;
	contract: Contract;
	questionnaire: JobQuestionnarie;
	questionnaireToSaveId = null;
	contractAndQuestionnaireData = null;
	contractToSave = null;
	paymentsToSave = null;
	paymentsFromServer = [];
	packagesNames;
	addonsNames;
	discountsNames;
	paymentScheduleNames;
	products: Array<Product> = [];
	productsNames = [];
	payments = [];
	issueDate = null;
	now: string;
	editQuote = false;
	quoteSummary;
	job;
	leadId: number;
	selectedPaymentSchedule;
	updatedProductSubscription;
	myDatePickerOptions: IMyDpOptions;
	unsubscribe: Subject<void> = new Subject<void>();
	dragulaPackagesOptions = {
		moves: (el, container, handle): boolean =>
			handle.classList.contains('handle-package')
	};
	dragulaProductsOptions = {
		moves: (el, container, handle): boolean =>
			handle.classList.contains('handle-package')
	};
	dragulaDiscountsOptions = {
		moves: (el, container, handle): boolean =>
			handle.classList.contains('handle-package')
	};

	constructor(
		private dragulaService: DragulaService,
		private stringModifierService: StringModifierServiceProvider,
		private quoteDataService: QuotesDataProvider,
		private messageService: MessageService,
		private timeService: TimeServiceProvider,
		private templatesDataService: TemplatesDataProvider,
		private questionnairesDataService: QuestionnairesDataProvider,
		private productPackagesDataService: ProductPackagesDataServiceProvider,
		private contractDataService: ContractsDataProvider,
		private quoteUpdateService: QuoteUpdateService,
		private paymentsSchedulesDataService: PaymentSchedulesDataProvider,
		private jobDataService: JobsDataServiceProvider,
		private leadDataService: LeadsDataServiceProvider,
		private settingsService: SettingsProvider,
		private location: Location,
		private quotesDataService: QuotesDataProvider
	) {}

	ngOnInit(): void {
		this.now = this.quoteUpdateService.getNow();
		if (this.quoteId && this.isJob) {
			this.getQuoteSourceData();
			this.editQuote = true;
		} else if (this.quoteId && !this.isJob) {
			this.getJobId(true);
			this.editQuote = true;
		} else if (!this.quoteId && this.isJob) {
			this.showNewQuote();
		} else {
			this.getJobId(false);
		}
		this.getProductsTemplates();
		this.getTimeSettings();
		this.setDragulaOptions();
		this.getPaymentsSchedules();
	}

	ngAfterViewInit(): void {
		this.registerUpdateProductSubscription();
	}

	ngOnDestroy(): void {
		this.clear();
	}

	// PRODUCT AND PACKAGES SECTION
	addNewPackage(): void {
		const productToAdd = JSON.parse(
			JSON.stringify(QuoteUpdateService.setEmptyProduct('PACKAGE'))
		);
		this.packages.push(productToAdd);
	}

	addNewAddon(): void {
		const productToAdd = JSON.parse(
			JSON.stringify(QuoteUpdateService.setEmptyProduct('ADD_ON'))
		);
		this.addons.push(productToAdd);
	}

	addNewDiscount(): void {
		const productToAdd = JSON.parse(
			JSON.stringify(QuoteUpdateService.setEmptyProduct('GIFT'))
		);
		this.discounts.push(productToAdd);
		this.updateQuoteSummary(true);
	}

	addNewProduct(): void {
		const productToAdd = JSON.parse(
			JSON.stringify(QuoteUpdateService.setEmptyProduct('ADD_ON'))
		);
		productToAdd.mandatory = true;
		this.products.push(productToAdd);
		this.updateQuoteSummary(true);
	}

	removePackage(itemToDelete): void {
		if (itemToDelete.type === 'package') {
			this.packages.splice(itemToDelete.index, 1);
			if (this.packages.length === 1) {
				this.packages[0].recommended = false;
			}
		} else if (itemToDelete.type === 'product') {
			this.addons.splice(itemToDelete.index, 1);
		}
	}

	removeDiscount(index) {
		this.discounts.splice(index, 1);
		this.updateQuoteSummary(true);
	}

	removeFixedQuoteProduct(itemToDelete): void {
		this.products.splice(itemToDelete.index, 1);
		this.updateQuoteSummary(true);
	}

	// CHANGE STATE SECTION
	setQuestionnaire(questionnaireId): void {
		this.questionnaireToSaveId = questionnaireId;
	}

	setContract(contract): void {
		this.contractToSave = contract;
	}

	disableButtons(isDisable): void {
		this.buttonDisable = isDisable;
	}

	setRecommended(index): void {
		this.packages.forEach((packageItem, packageIndex): void => {
			if (packageIndex !== index) {
				packageItem.recommended = false;
			}
		});
	}

	setPaymentScheduleId(payments): void {
		this.paymentScheduleTemplateId = payments.id;
		this.paymentsToSave = payments.payments;
	}

	setNewProduct(product): void {
		if (product.type === 'package') {
			this.packages.forEach((packageItem, index) => {
				if (index === product.index) {
					this.packages[index] = JSON.parse(
						JSON.stringify(Object.assign({}, product.product))
					);
				}
			});
		} else if (product.type === 'discount') {
			this.discounts.forEach((discount, index) => {
				if (index === product.index) {
					this.discounts[index] = JSON.parse(
						JSON.stringify(Object.assign({}, product.product))
					);
				}
			});
		} else {
			this.addons.forEach((addon, index) => {
				if (index === product.index) {
					this.addons[index] = JSON.parse(
						JSON.stringify(Object.assign({}, product.product))
					);
				}
			});
		}
	}

	setNewFixedQuoteProduct(product): void {
		this.products.forEach((packageItem, index) => {
			if (index === product.index) {
				this.products[index] = JSON.parse(
					JSON.stringify(Object.assign({}, product.product))
				);
			}
		});
	}

	updateFixedQuoteProductProperties(product): void {
		this.products.forEach((packageItem, index) => {
			if (index === product.index) {
				this.products[index].price = product.product.price;
				this.products[index].quantity = product.product.quantity;
				this.products[index].discount = product.product.discount;
				this.products[index].taxType = product.product.taxType;
				this.products[index].amount = product.product.amount;
			}
		});
	}

	updateQuoteSummary(needToUpdatePayments): void {
		// setTimeout fixes issue on Safari when id of product changes with delay

		setTimeout(() => {
			const productsToCalculate =
				this.quoteType === 'custom'
					? this.getProductsToSave(false)
					: this.getProductsToSaveFixedQuote(false);
			if (productsToCalculate && this.currencyAndTax) {
				this.quotesDataService
					.calculateProductsSummary(
						productsToCalculate,
						this.currencyAndTax.percentage,
						this.currencyAndTax.currency.symbol
					)
					.subscribe(
						data =>
							this.successUpdatePricesCalculation(
								data,
								needToUpdatePayments,
								productsToCalculate
							),
						error => this.messageService.showDefaultErrorMessage(error)
					);
			}
		}, 0);
	}

	private successUpdatePricesCalculation(
		data: any,
		needToUpdatePayments,
		productsToCalculate
	): void {
		this.quoteSummary = data;
		this.quoteSummary.taxName = this.currencyAndTax.taxName;
		if (
			this.job &&
			this.quote &&
			this.paymentScheduleNames &&
			needToUpdatePayments &&
			productsToCalculate.length > 0
		) {
			this.paymentScheduleComponent.getPaymentScheduleNew(
				this.paymentScheduleTemplateId,
				true,
				this.quoteSummary.total
			);
		} else if (
			this.quoteType &&
			this.quoteSummary &&
			this.quoteSummary.total === 0
		) {
			setTimeout(() => {
				this.paymentScheduleComponent.hideAndResetPaymentSchedule();
			}, 500);
		}
	}

	registerUpdateProductSubscription(): void {
		this.quoteUpdateService.updatedProductObservable
			.pipe(
				takeUntil(this.unsubscribe),
				filter((product): boolean => product !== null)
			)
			.subscribe(updatedProduct => {
				if (this.quoteType === 'fixed') {
					this.updateFixedQuoteProductProperties(updatedProduct);
				} else {
					this.formProducts();
				}

				if (
					this.quoteUpdateService.validateProductsPropsDefined(this.products)
				) {
					this.updateQuoteSummary(true);
				}
			});
	}

	private formProducts() {
		this.products = [];
		this.packages.forEach(packageItem => {
			this.products.push(packageItem);
		});
		this.addons.forEach(addon => {
			this.products.push(addon);
		});

		this.discounts.forEach(discount => {
			this.products.push(discount);
		});
	}

	// SAVE QUOTE SECTION
	saveQuote(): void {
		this.buttonDisable = true;
		if (!this.validateQuote()) {
			this.buttonDisable = false;
			return;
		}

		const data = this.gatherDataToSaveQuote();

		if (this.editQuote) {
			this.updateQuote(data);
		} else {
			this.createQuote(data);
		}
	}

	private validateQuote(): boolean {
		const products =
			this.quoteType === 'custom'
				? this.getProductsToSave(false)
				: this.getProductsToSaveFixedQuote(false);
		let invalidProductType;
		if (
			products.find(
				product => product.type === 'GIFT' && !product.mandatory
			) !== undefined
		) {
			this.messageService.showToaster('e_6', 'editQuote');
			return false;
		}
		if (
			products.find(
				product => product.type === 'GIFT' && parseInt(product.quantity) !== 1
			) !== undefined
		) {
			this.messageService.showToaster('e_10', 'editQuote');
			return false;
		}
		if (
			products.find(
				product => product.type === 'GIFT' && product.price >= 0
			) !== undefined
		) {
			this.messageService.showToaster('e_16', 'editQuote');
			return false;
		}
		if (
			products.find(product => product.type === 'GIFT') !== undefined &&
			products.find(product => product.taxType !== products[0].taxType) !==
				undefined
		) {
			this.messageService.showToaster('e_13', 'editQuote');
			return false;
		}
		if (products.length === 0) {
			this.messageService.showToaster('e_4', 'editQuote');
			return false;
		}
		if (
			this.quote.ponumber !== null &&
			!this.quote.ponumber.match(
				'^[a-zA-Z0-9 *№\'ʼ;:%!?@#$&|>\\<{}()-`.+,/"]*$'
			)
		) {
			this.messageService.showToaster('e_7', 'editQuote');
			return false;
		}
		if (
			products.find(product => {
				if (product.name === '') {
					invalidProductType = product.type;
					if (invalidProductType === 'GIFT') {
						invalidProductType = 'discount';
					} else if (invalidProductType === 'ADD_ON') {
						invalidProductType = 'product';
					}
					return true;
				}
			})
		) {
			this.messageService.showToaster(
				'e_0',
				'productsPackages',
				invalidProductType.toLowerCase()
			);
			return false;
		}
		if (products.find(product => product.quantity <= 0) !== undefined) {
			this.messageService.showToaster('e_8', 'editQuote');
			return false;
		}
		if (
			products.find(product => {
				if (
					product.type !== 'GIFT' &&
					(product.description === '' || product.description.length === 7)
				) {
					invalidProductType =
						product.type.toUpperCase() === 'PACKAGE' ? 'package' : 'product';
					return true;
				}
			})
		) {
			this.messageService.showToaster('e_9', 'editQuote', invalidProductType);
			return false;
		}
		if (
			products.find(product => {
				if (product.name.trim().length === 0) {
					if (product.type.toUpperCase() === 'GIFT') {
						invalidProductType = 'Discount';
					} else {
						invalidProductType =
							product.type.toUpperCase() === 'PACKAGE' ? 'Package' : 'Product';
					}
					return true;
				}
			})
		) {
			this.messageService.showToaster('e_14', 'editQuote', invalidProductType);
			return false;
		}
		if (products.find(product => product.discount > 100) !== undefined) {
			this.messageService.showToaster('e_2', 'editQuote');
			return false;
		}
		if (
			this.paymentsToSave.find(
				payment =>
					payment.date && (payment.date === null || payment.date.jsdate === '')
			) !== undefined
		) {
			this.messageService.showToaster('e_11', 'editQuote');
			return false;
		}

		const invalidCounter = document.querySelector('.counter.red');
		if (invalidCounter) {
			this.messageService.showToaster('e_6', 'productsPackages', '4000');
			return false;
		}
		return true;
	}

	private getTimeSettings(): void {
		this.settingsService.timeFormatSettings
			.takeUntil(this.unsubscribe)
			.subscribe((data): void => {
				switch (data.dateFormat) {
					case 'DD_MM_YYYY':
						this.datePlaceHolder = 'dd/mm/yyyy';
						break;
					case 'MM_DD_YYYY':
						this.datePlaceHolder = 'mm/dd/yyyy';
						break;
					default:
						this.datePlaceHolder = '';
						break;
				}
			});
	}

	private getJobId(makeQuoteRequest): void {
		this.leadId = this.idToGetQuote;
		this.quoteDataService.getJobId(this.idToGetQuote).subscribe(
			(data): void => {
				this.idToGetQuote = data;
				if (makeQuoteRequest) {
					this.getQuoteSourceData();
				} else {
					this.showNewQuote();
				}
			},
			(error): void => this.messageService.showDefaultErrorMessage(error)
		);
	}

	// DRAG'N'DROP SECTION
	private setDragulaOptions(): void {
		this.dragulaService.setOptions('products', this.dragulaProductsOptions);
		this.dragulaService.setOptions('packages', this.dragulaPackagesOptions);
		this.dragulaService.setOptions('discounts', this.dragulaDiscountsOptions);
	}

	private clear(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
		this.quoteUpdateService.setNextUpdateProduct(null, null);
		this.dragulaService.destroy('products');
		this.dragulaService.destroy('packages');
		this.dragulaService.destroy('discounts');
	}

	// GET QUOTE DATA SECTION
	private getQuoteSourceData(): void {
		this.quoteDataService
			.getQuoteToEditV1(this.idToGetQuote, this.quoteId)
			.subscribe(
				(data): void => {
					this.successGetQuoteData(data);
				},
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	changeAutoGenerateInvoice(val: boolean): void {
		this.autoGenerateInvoice = val;
	}

	private setPackagesAndAddons(products): void {
		const sortedProducts = QuoteUpdateService.sortProducts(products);
		if (this.quoteType === 'custom') {
			const {
				packages,
				addons,
				discounts
			} = QuoteUpdateService.setPackagesAndAddons(sortedProducts);
			this.packages = packages;
			this.addons = addons;
			this.products = sortedProducts;
			this.discounts = discounts;
		} else {
			const {
				addons,
				discounts
			} = QuoteUpdateService.separateDiscountsAndProducts(sortedProducts);
			this.products = addons;
			this.discounts = discounts;
		}
	}

	private setPackagesAndAddonsNames(products): void {
		const {
			packages,
			addons,
			discounts
		} = QuoteUpdateService.setPackagesAndAddons(products);
		this.packagesNames = packages;
		this.addonsNames = addons;
		this.discountsNames = discounts;
	}

	private showNewQuote(): void {
		this.paymentScheduleTemplateId = -1;
		this.selectedPaymentSchedule = null;
		this.getQuoteIdAndDate(this.idToGetQuote);

		this.getCurrencyAndTaxes();

		if (this.isJob) {
			this.getJobInfo();
		} else {
			this.getLeadInfo();
		}

		this.getContractAndQuestionnaireInfo();
	}

	private getJobInfo(): void {
		this.jobDataService
			.getJobInfo(this.idToGetQuote)
			.subscribe(
				data => (this.job = data),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private getLeadInfo(): void {
		this.leadDataService
			.getLeadInfo(this.leadId)
			.subscribe(
				(data): void => this.successGetLead(data),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private successGetLead(data): void {
		this.job = data.job;
	}

	private getCurrencyAndTaxes(): void {
		this.settingsService.getCurrencyAndTaxes().subscribe(
			(data): void => {
				this.successGetCurrencyAndTaxes(data);
			},
			(error): void => this.messageService.showDefaultErrorMessage(error)
		);
	}

	private successGetCurrencyAndTaxes(data): void {
		this.currencyAndTax = {
			currency: {
				symbol: data.currencyDTO.symbol
			},
			percentage: data.percentage,
			taxName: data.taxName
		};

		if (this.quoteType === 'custom' && !this.editQuote) {
			this.addNewPackage();
		} else if (this.quoteType !== 'custom' && !this.editQuote) {
			this.addNewProduct();
		}
	}

	private getDefaultIntroduction(): void {
		this.productPackagesDataService
			.getDefaultIntroductionText()
			.subscribe(
				(data): void => (this.quote.introduction = data.description),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private setQuoteSources(quote): void {
		const {
			contract,
			currencyAndTax,
			paymentSchedule,
			payments,
			questionnaire
		} = quote;

		this.currencyAndTax = currencyAndTax;
		if (paymentSchedule === null) {
			this.paymentScheduleTemplateId = -1;
		} else {
			this.paymentScheduleIdFromServer = paymentSchedule.id;
		}
		this.payments = payments;
		this.paymentsFromServer = payments;
		this.paymentSchedule = paymentSchedule;

		if (contract !== null) {
			this.contract = contract;
		}
		if (questionnaire !== null) {
			this.questionnaireToSaveId = questionnaire.id;
			this.questionnaire = questionnaire;
		}

		this.getContractAndQuestionnaireInfo();
	}

	private getContractAndQuestionnaireInfo(): void {
		Observable.zip(
			this.templatesDataService.getTemplates('contract_preview'),
			this.templatesDataService.getTemplates('questionnaire_preview'),
			this.getContract()
		).subscribe(
			([contractTemplates, questionnaireTemplates, contractData]): void => {
				this.contractAndQuestionnaireData = {
					contract: contractData ? contractData.contract : null,
					questionnaire: this.questionnaire,
					contractTemplates: contractTemplates.contractTemplates,
					questionnaireTemplates: questionnaireTemplates.questionnaireTemplates
				};
			},
			(error): void => this.messageService.showDefaultErrorMessage(error)
		);
	}

	private getContract(): Observable<any> {
		if (this.contract) {
			return this.contractDataService.getContract(
				this.job.id,
				this.contract.id
			);
		}
		return new Observable((observer): void => {
			observer.next(null);
		});
	}

	private back(): void {
		this.location.back();
	}

	private updateQuote(data): void {
		this.updateQuoteRequest(data)
			.finally((): void => {
				this.buttonDisable = false;
			})
			.subscribe(
				(quoteData): void => {
					if (quoteData.status >= 400 && quoteData.status <= 500) {
						this.errorSaveQuote(quoteData);
					} else {
						this.successSaveQuote(quoteData);
					}
				},
				(error): void => this.errorSaveQuote(error)
			);
	}

	private updateQuoteRequest(data): Observable<any> {
		if (this.quoteType === 'custom') {
			return this.quoteDataService.saveQuoteV1(
				this.idToGetQuote,
				this.quote.id,
				data
			);
		}
		return this.quoteDataService.saveFixedQuoteV1(
			this.idToGetQuote,
			this.quote.id,
			data
		);
	}

	private errorSaveQuote(error): void {
		if (error.error.errorMessage === 'Requested resource not found') {
			this.messageService.showToaster('e_0', 'editQuote');
		} else if (
			error.error.errorMessage ===
			'This Quote ID is already used. Please enter another one'
		) {
			this.messageService.showToaster('e_15', 'editQuote');
		} else {
			this.messageService.showErrorFromBackend(
				error.error.errorTitle,
				error.error.errorMessage
			);
		}
	}

	private successSaveQuote(data): void {
		this.messageService.showToaster('s_2', 'payments');
		if (this.isJob) {
			this.location.go(`jobs/${this.idToGetQuote}/quote/${data.id}`);
		} else {
			this.location.go(`leads/${this.leadId}/quote/${data.id}`);
		}
		(<any>window).Intercom('trackEvent', 'created quote');
	}

	private createQuote(data): void {
		this.createQuoteRequest(data)
			.finally((): void => {
				this.buttonDisable = false;
			})
			.subscribe(
				(data): void => {
					if (data.status >= 400 && data.status <= 500) {
						this.errorSaveQuote(data);
					} else {
						this.successSaveQuote(data);
					}
				},
				({ error }): void =>
					this.messageService.showErrorFromBackend(
						error.errorTitle,
						error.errorMessage
					)
			);
	}

	private createQuoteRequest(data): Observable<any> {
		if (this.quoteType === 'custom') {
			return this.quoteDataService.createQuoteV1(this.idToGetQuote, data);
		}
		return this.quoteDataService.createFixedQuoteV1(this.idToGetQuote, data);
	}

	private successGetQuoteData(quote): void {
		const { job, products } = quote;
		this.quote = JSON.parse(JSON.stringify(quote));
		this.job = job;
		if (!this.quoteType) {
			this.quoteType =
				quote.quoteType === 'CHOOSE_AND_PICK'
					? 'custom'
					: quote.quoteType.toLowerCase();
		}
		if (quote.paymentSchedule && quote.paymentSchedule.id) {
			this.job.paymentScheduleId = quote.paymentSchedule.id;
		}
		this.issueDate = this.quoteUpdateService.setQuoteIssueDate(quote.issueDate);
		this.autoGenerateInvoice = this.quote.generateInvoiceWhenAccepted;

		this.setQuoteSources(quote);
		this.setPackagesAndAddons(products);

		this.updateQuoteSummary(false);

		if (quote.status === 'ACCEPTED') {
			this.back();
		}
	}

	private setPaymentScheduleIdForSave(id) {
		switch (+id) {
			case 0:
				return this.paymentScheduleIdFromServer;
			case -1:
				return null;
			default:
				return this.paymentScheduleTemplateId;
		}
	}

	private getContractToSave(): Contract {
		if (this.contractToSave) {
			this.contractToSave.name = this.stringModifierService.cutSelectedText(
				this.contractToSave.name
			);
			/* Fix changed field for GDPR */
			if (this.contractToSave.optInConsentStatement) {
				this.contractToSave.optInConsent = this.contractToSave.optInConsentStatement;
				delete this.contractToSave.optInConsentStatement;
			}
			/*
			Fix backend grammar mistake
			showOTPinconsent => showOPTinconsent
			 */
			if (this.contractToSave.showOtpInConsent) {
				this.contractToSave.showOptInConsent = this.contractToSave.showOtpInConsent;
				delete this.contractToSave.showOtpInConsen;
			}
		}
		return this.contractToSave;
	}

	private getPaymentsToSave(): Array<QuotePayments> {
		return this.paymentsToSave.map((payment): void => {
			let paymentToSave;
			if (payment.date) {
				paymentToSave = Object.assign(payment, {
					paymentDate: this.quoteUpdateService.getDate(payment.date.jsdate)
				});
			} else {
				paymentToSave = payment;
			}
			return paymentToSave;
		});
	}

	private getProductsToSave(needToRemoveId): Array<Product> {
		const productsToSave = this.packages.map((packageItem, index): Product =>
			Object.assign(packageItem, {
				id: packageItem.fromSettings && needToRemoveId ? null : packageItem.id,
				order: index,
				price: needToRemoveId
					? this.stringModifierService.convertAmountIntoNumber(
							packageItem.price
					  )
					: packageItem.price,
				mandatory: this.packages.length > 1 ? false : packageItem.mandatory
			})
		);

		this.addons.forEach((product, index): void => {
			productsToSave.push(
				Object.assign(product, {
					id: product.fromSettings && needToRemoveId ? null : product.id,
					order: index,
					price: needToRemoveId
						? this.stringModifierService.convertAmountIntoNumber(product.price)
						: product.price
				})
			);
		});
		this.discounts.forEach((discount, index): void => {
			productsToSave.push(
				Object.assign(discount, {
					id: discount.fromSettings && needToRemoveId ? null : discount.id,
					order: index,
					price: needToRemoveId
						? this.stringModifierService.convertAmountIntoNumber(discount.price)
						: discount.price,
					taxType: this.setTaxTypeForDiscount(productsToSave, discount.taxType)
				})
			);
		});
		return productsToSave;
	}

	private gatherDataToSaveQuote(): any {
		let isQuestionnaireChanged = true;
		if (
			this.questionnaire &&
			+this.questionnaireToSaveId === this.questionnaire.id
		) {
			isQuestionnaireChanged = false;
		}
		this.paymentsToSave = this.getPaymentsToSave();
		return {
			quote: {
				quoteId: this.quote.quoteId,
				ponumber: this.quote.ponumber,
				issueDate: this.timeService.getDateInFormat(
					this.issueDate.jsdate,
					'date'
				),
				notes: this.quote.notes,
				introduction: this.quote.introduction,
				generateInvoiceWhenAccepted: this.autoGenerateInvoice,
				paymentSchedulerTemplateId: this.setPaymentScheduleIdForSave(
					this.paymentScheduleTemplateId
				),
				products:
					this.quoteType === 'custom'
						? this.getProductsToSave(true)
						: this.getProductsToSaveFixedQuote(true),
				payments: this.paymentsToSave
			},
			removeContract: this.contractToSave === null,
			removeQuestionnaire: this.questionnaireToSaveId === '-1',
			contract: this.getContractToSave(),
			questionnaire: isQuestionnaireChanged
				? {
						templateId: this.questionnaireToSaveId
				  }
				: null
		};
	}

	setTaxTypeForDiscount(productsToSave, previousTax) {
		return productsToSave[0] ? productsToSave[0].taxType : previousTax;
	}

	private getProductsToSaveFixedQuote(needToRemoveId): Array<Product> {
		this.products = QuoteUpdateService.setFixedQuoteProducts(this.products);
		const productsToSave = this.products.map((productItem, index): Product =>
			Object.assign(productItem, {
				id: productItem.fromSettings && needToRemoveId ? null : productItem.id,
				order: index,
				price: needToRemoveId
					? this.stringModifierService.convertAmountIntoNumber(
							productItem.price
					  )
					: productItem.price
			})
		);

		this.discounts.forEach((discount, index): void => {
			productsToSave.push(
				Object.assign(discount, {
					id: discount.fromSettings && needToRemoveId ? null : discount.id,
					order: index,
					price: needToRemoveId
						? this.stringModifierService.convertAmountIntoNumber(discount.price)
						: discount.price,
					taxType: this.setTaxTypeForDiscount(productsToSave, discount.taxType)
				})
			);
		});
		return productsToSave;
	}

	private getProductsTemplates(): void {
		const pageRequest = QuoteUpdateService.getPageRequest();
		this.productPackagesDataService
			.getPackagesOrProducts(pageRequest)
			.subscribe(
				(data): void => {
					this.productsNames = data.items;
					this.setPackagesAndAddonsNames(data.items);
				},
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private getPaymentsSchedules(): void {
		const params = {
			perPage: 500,
			page: 1,
			predicate: 'name',
			reverse: false
		};
		this.paymentsSchedulesDataService
			.getPaymentsSchedules(params)
			.subscribe(
				(data): void => (this.paymentScheduleNames = data.results),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private getQuoteIdAndDate(id: number): void {
		this.quoteDataService
			.getQuoteIdAndDate(id)
			.takeUntil(this.unsubscribe)
			.subscribe(data => {
				this.quoteId = data.quoteId;
				this.issueDate = this.quoteUpdateService.setQuoteIssueDate(
					data.issueDate
				);

				this.quote = this.quoteUpdateService.setEmptyQuote(this.quoteId);

				this.getDefaultIntroduction();
			});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/quotes/quote-update/quote-update.ts